
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Snackbar,
    TextField,
    Typography
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";

import { currentMonth } from "../../../services/dashBoardService";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import {
    getEgaSectionData,
    saveEgaSectionData
} from "../../../services/engaSectionService";
import PageHeader from "../../../utils/PageHeader";


// ======================================================
// INITIAL FORM
// ======================================================

const createInitialForm = () => {
    return {};
};


// ======================================================
// MAIN COMPONENT
// ======================================================

export default function EgaSection() {

    // ==================================================
    // MONTH / YEAR
    // ==================================================

    const [monthData, setMonthData] =
        useState(null);

    const [month, setMonth] =
        useState(null);

    const [year, setYear] =
        useState(null);


    // ==================================================
    // DATA
    // ==================================================

    const [rows, setRows] =
        useState([]);

    const [formData, setFormData] =
        useState(
            createInitialForm()
        );


    // ==================================================
    // LOADING
    // ==================================================

    const [loading, setLoading] =
        useState(false);

    const [saving, setSaving] =
        useState(false);


    // ==================================================
    // MESSAGE
    // ==================================================

    const [message, setMessage] =
        useState({

            open: false,

            text: "",

            severity: "success"

        });


    // ==================================================
    // CURRENT MONTH
    // ==================================================

    const loadCurrentMonth =
        useCallback(async () => {

            try {

                const res =
                    await currentMonth();

                const data =
                    res.data.data;

                setMonthData(data);

                setMonth(data.month);

                setYear(data.year);

            } catch (err) {

                console.error(
                    "Current month error:",
                    err
                );

            }

        }, []);


    // ==================================================
    // INITIAL LOAD
    // ==================================================

    useEffect(() => {

        loadCurrentMonth();

    }, [loadCurrentMonth]);


    // ==================================================
    // MONTH NAME
    // ==================================================

    const monthName = useMemo(() => {

        if (
            !month ||
            !year
        ) {
            return "";
        }

        return new Date(

            year,

            month - 1,

            1

        ).toLocaleString(

            "en-US",

            {
                month: "long"
            }

        );

    }, [
        month,
        year
    ]);


    // ==================================================
    // SHOW MESSAGE
    // ==================================================

    const showMessage = (
        text,
        severity = "success"
    ) => {

        setMessage({

            open: true,

            text,

            severity

        });

    };


    // ==================================================
    // LOAD DATA
    // ==================================================

    const loadData = async () => {

        if (
            !month ||
            !year
        ) {
            return;
        }

        try {

            setLoading(true);

            const response =
                await getEgaSectionData(
                    month,
                    year
                );


            if (
                !response?.success
            ) {

                showMessage(

                    response?.message ||
                    "Unable to load data",

                    "error"

                );

                return;

            }


            const apiRows =
                response.data || [];


            setRows(apiRows);


            const newForm = {};


            apiRows.forEach(
                (row) => {

                    newForm[
                        row.SalaryCode
                    ] = {

                        mainSalary:
                            Number(
                                row.MainSalary ||
                                0
                            ),

                        tuitionFees:
                            Number(
                                row.TutionFees ||
                                0
                            )

                    };

                }
            );


            setFormData(
                newForm
            );


        } catch (error) {

            console.error(
                "Load EGA error:",
                error
            );

            showMessage(

                error?.response?.data?.message ||

                "Unable to load EGA data",

                "error"

            );

        } finally {

            setLoading(false);

        }

    };


    // ==================================================
    // LOAD WHEN MONTH / YEAR CHANGES
    // ==================================================

    useEffect(() => {

        if (

            month >= 1 &&

            month <= 12 &&

            year >= 2000

        ) {

            loadData();

        }

    }, [
        month,
        year
    ]);


    // ==================================================
    // CHANGE INPUT
    // ==================================================

    const handleChange = (

        salaryCode,

        field,

        value

    ) => {

        // Only digits and decimal point

        const cleaned =
            value.replace(
                /[^0-9.]/g,
                ""
            );


        setFormData(
            (prev) => ({

                ...prev,

                [salaryCode]: {

                    ...prev[
                        salaryCode
                    ],

                    [field]:

                        cleaned === ""

                            ? 0

                            : Number(
                                cleaned
                            )

                }

            })
        );

    };


    // ==================================================
    // ROW TOTAL
    // ==================================================

    const getRowTotal = (
        salaryCode
    ) => {

        const row =
            formData[
                salaryCode
            ];


        if (!row) {
            return 0;
        }


        return (

            Number(
                row.mainSalary ||
                0
            )

            +

            Number(
                row.tuitionFees ||
                0
            )

        );

    };



const getAmountByDescription = (description) => {
    const row = rows.find(
        (item) =>
            String(item.SalaryDiscription || "")
                .trim()
                .toLowerCase() === description.toLowerCase()
    );

    return row ? getRowTotal(row.SalaryCode) : 0;
};


    // ==================================================
    // MECHANICAL TOTAL
    // ==================================================

    const mechanicalTotal = useMemo(() => {
    const mechanical = getAmountByDescription("Mechanical");
    const pco = getAmountByDescription("PCO");
    const incentive = getAmountByDescription("Incentive Mechanical");

    return mechanical + pco - incentive;
}, [rows, formData]);

    // ==================================================
    // ELECTRICAL TOTAL
    // ==================================================

   const electricalTotal = useMemo(() => {
    const electrical = getAmountByDescription("Electrical");
    const incentive = getAmountByDescription("Incentive Electrical");

    return electrical - incentive;
}, [rows, formData]);

    // ==================================================
    // GRAND TOTAL
    // ==================================================

    
const grandTotal = useMemo(() => {
    const rpf = getAmountByDescription("RPF");
    const shopClerk = getAmountByDescription("Shop Clerk");
    const btc = getAmountByDescription("BTC");
    const cntxr = getAmountByDescription("CNTXR");

    return (
        mechanicalTotal +
        electricalTotal +
        rpf +
        shopClerk +
        btc +
        cntxr
    );
}, [rows, formData, mechanicalTotal, electricalTotal]);


    // ==================================================
    // SAVE
    // ==================================================

    const handleSave = async () => {

        try {

            setSaving(true);


            const data =
                rows.map(
                    (row) => ({

                        SalaryCode:
                            Number(
                                row.SalaryCode
                            ),

                        MainSalary:
                            Number(

                                formData[
                                    row.SalaryCode
                                ]?.mainSalary ||

                                0

                            ),

                        TutionFees:

                            row.HasTuitionFees

                                ? Number(

                                    formData[
                                        row.SalaryCode
                                    ]?.tuitionFees ||

                                    0

                                )

                                : 0

                    })
                );


            const payload = {

                month:
                    Number(month),

                year:
                    Number(year),

                data

            };


            const response =
                await saveEgaSectionData(
                    payload
                );


            if (
                !response?.success
            ) {

                showMessage(

                    response?.message ||

                    "Unable to save data",

                    "error"

                );

                return;

            }


            showMessage(

                "EGA section data saved successfully",

                "success"

            );


            await loadData();


        } catch (error) {

            console.error(
                "Save EGA error:",
                error
            );

            showMessage(

                error?.response?.data?.message ||

                "Error while saving EGA data",

                "error"

            );

        } finally {

            setSaving(false);

        }

    };


    // ==================================================
    // FORMAT NUMBER
    // ==================================================

    const formatNumber = (
        value
    ) => {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN"
        );

    };


    // ==================================================
    // SECTION ROW
    // ==================================================

    const renderSalaryRow = (
        row
    ) => {

        const hasTuition =
            Number(
                row.HasTuitionFees
            ) === 1;


        const salaryData =
            formData[
                row.SalaryCode
            ] || {};


        const total =
            getRowTotal(
                row.SalaryCode
            );


        return (

            <Box
                key={
                    row.SalaryCode
                }

                className="ega-salary-row"

                sx={{

                    display: "grid",

                    gridTemplateColumns: {

                        xs:
                            hasTuition

                                ? "minmax(0, 1fr) minmax(95px, 1fr)"

                                : "minmax(0, 1fr) minmax(95px, 1fr)",

                        sm:
                            hasTuition

                                ? "1.5fr 1fr 1fr 1fr"

                                : "1.5fr 1fr 1fr 1fr",

                        md:
                            "1.5fr 1fr 1fr 1fr"

                    },

                    gap: {

                        xs: 1,

                        sm: 1,

                        md: 1.25

                    },

                    alignItems:
                        "center",

                    px: {

                        xs: 1,

                        sm: 1

                    },

                    py: {

                        xs: 1,

                        sm: 0.65

                    },

                    borderBottom:
                        "1px solid #e5e5e5",

                    backgroundColor:
                        "#fff"

                }}
            >

                {/* ==================================================
                    DESCRIPTION
                ================================================== */}

                <Box
                    sx={{

                        minWidth: 0,

                        gridColumn: {

                            xs:
                                hasTuition
                                    ? "1 / -1"
                                    : "1",

                            sm:
                                "auto"

                        }

                    }}
                >

                    <Typography
                        sx={{

                            color:
                                "#ed1740",

                            fontWeight:
                                600,

                            fontSize: {

                                xs:
                                    "0.82rem",

                                sm:
                                    "0.875rem",

                                md:
                                    "0.9rem"

                            },

                            lineHeight: 1.3,

                            wordBreak:
                                "break-word"

                        }}
                    >

                        {
                            row.SalaryDiscription
                        }

                    </Typography>

                </Box>


                {/* ==================================================
                    PAY + ALLOWANCES
                ================================================== */}

                <Box
                    sx={{

                        minWidth: 0

                    }}
                >

                    <Typography
                        sx={{

                            display: {

                                xs:
                                    "block",

                                sm:
                                    "none"

                            },

                            fontSize:
                                "0.7rem",

                            fontWeight:
                                600,

                            color:
                                "text.secondary",

                            mb: 0.25

                        }}
                    >
                        Pay + Allowances
                    </Typography>


                    <TextField

                        fullWidth

                        size="small"

                        type="number"

                        value={
                            salaryData.mainSalary ??
                            0
                        }

                        onChange={(e) =>
                            handleChange(

                                row.SalaryCode,

                                "mainSalary",

                                e.target.value

                            )
                        }

                        inputProps={{
                            min: 0,
                            inputMode:
                                "decimal"
                        }}

                        sx={{

                            "& .MuiInputBase-root":
                                {

                                    backgroundColor:
                                        "#fff"

                                },

                            "& input":
                                {

                                    textAlign:
                                        "right",

                                    fontSize: {

                                        xs:
                                            "0.85rem",

                                        sm:
                                            "0.875rem"

                                    },

                                    padding: {

                                        xs:
                                            "8px 7px",

                                        sm:
                                            "8px"

                                    }

                                }

                        }}

                    />

                </Box>


                {/* ==================================================
                    TUITION
                ================================================== */}

                {hasTuition ? (

                    <Box
                        sx={{

                            minWidth: 0

                        }}
                    >

                        <Typography
                            sx={{

                                display: {

                                    xs:
                                        "block",

                                    sm:
                                        "none"

                                },

                                fontSize:
                                    "0.7rem",

                                fontWeight:
                                    600,

                                color:
                                    "text.secondary",

                                mb: 0.25

                            }}
                        >
                            Tuition Fees
                        </Typography>


                        <TextField

                            fullWidth

                            size="small"

                            type="number"

                            value={
                                salaryData.tuitionFees ??
                                0
                            }

                            onChange={(e) =>
                                handleChange(

                                    row.SalaryCode,

                                    "tuitionFees",

                                    e.target.value

                                )
                            }

                            inputProps={{
                                min: 0,
                                inputMode:
                                    "decimal"
                            }}

                            sx={{

                                "& .MuiInputBase-root":
                                    {

                                        backgroundColor:
                                            "#fff"

                                    },

                                "& input":
                                    {

                                        textAlign:
                                            "right",

                                        fontSize: {

                                            xs:
                                                "0.85rem",

                                            sm:
                                                "0.875rem"

                                        },

                                        padding: {

                                            xs:
                                                "8px 7px",

                                            sm:
                                                "8px"

                                        }

                                    }

                            }}

                        />

                    </Box>

                ) : (

                    <Box
                        sx={{

                            display: {

                                xs:
                                    "none",

                                sm:
                                    "block"

                            }

                        }}
                    />

                )}


                {/* ==================================================
                    TOTAL
                ================================================== */}

                <Box
                    sx={{

                        minWidth: 0

                    }}
                >

                    <Typography
                        sx={{

                            display: {

                                xs:
                                    "block",

                                sm:
                                    "none"

                            },

                            fontSize:
                                "0.7rem",

                            fontWeight:
                                600,

                            color:
                                "text.secondary",

                            mb: 0.25

                        }}
                    >
                        Total
                    </Typography>


                    <TextField

                        fullWidth

                        size="small"

                        value={
                            formatNumber(
                                total
                            )
                        }

                        InputProps={{
                            readOnly: true
                        }}

                        sx={{

                            "& .MuiInputBase-root":
                                {

                                    backgroundColor:
                                        "#f1f1f1"

                                },

                            "& input":
                                {

                                    textAlign:
                                        "right",

                                    fontWeight:
                                        700,

                                    fontSize: {

                                        xs:
                                            "0.85rem",

                                        sm:
                                            "0.875rem"

                                    },

                                    padding: {

                                        xs:
                                            "8px 7px",

                                        sm:
                                            "8px"

                                    }

                                }

                        }}

                    />

                </Box>

            </Box>

        );

    };


    // ==================================================
    // RENDER
    // ==================================================

    return (

        <Box
              sx={{
                p: {
                  xs: 1,
                  sm: 2,
                  md: 3,
                },
              }}
            >

            <Box
              sx={{
                p: {
                  xs: 1,
                  sm: 2,
                  md: 3,
                },
              }}
            >


                {/* ==================================================
                    HEADER
                ================================================== */}

                 <PageHeader
        title="EGA/ENGA Section Data Entry"
        subtitle={`Data entry status for the month of: ${monthName} - ${year}`}
        icon={EditCalendarIcon}
       // statusLabel={'Total Amount'}
       //  statusValue="CSV File"
        statusChip={{
          label: loading
            ? "Loading..."
            : grandTotal > 0
              ? `Grand Total: ${formatNumber(grandTotal)}`
              : "No Data",
          color: loading ? "warning" : grandTotal > 0 ? "primary" : "default",
        }}


// {formatNumber(
//                                     grandTotal
//                                 )}



       // showRefresh
       // onRefresh={handleRefresh}
        // refreshing={loading}
      />


                {/* ==================================================
                    MAIN DATA CARD
                ================================================== */}

                <Card
                    elevation={3}
                    sx={{

                        width: "100%",

                        borderRadius: {

                            xs: 1.5,

                            sm: 2

                        },

                        overflow:
                            "hidden"

                    }}
                >

                    <CardContent
                        sx={{

                            p: {

                                xs: 0.75,

                                sm: 1.25,

                                md: 1.5

                            }

                        }}
                    >


                        {/* ==================================================
                            COLUMN HEADER - DESKTOP
                        ================================================== */}

                        <Box
                            sx={{

                                display: {

                                    xs:
                                        "none",

                                    sm:
                                        "grid"

                                },

                                gridTemplateColumns:
                                    "1.5fr 1fr 1fr 1fr",

                                gap: 1,

                                alignItems:
                                    "center",

                                px: 1,

                                py: 1,

                                backgroundColor:
                                    "#fafafa",

                                border:
                                    "1px solid #ddd",

                                borderBottom:
                                    "2px solid #ccc"

                            }}
                        >

                            <Typography />

                            <Typography
                                align="center"
                                sx={{

                                    color:
                                        "#ed1740",

                                    fontWeight:
                                        "bold",

                                    fontSize:
                                        14

                                }}
                            >
                                Pay + Allowances
                            </Typography>


                            <Typography
                                align="center"
                                sx={{

                                    color:
                                        "#ed1740",

                                    fontWeight:
                                        "bold",

                                    fontSize:
                                        14

                                }}
                            >
                                Tuition Fees
                            </Typography>


                            <Typography
                                align="center"
                                sx={{

                                    color:
                                        "#ed1740",

                                    fontWeight:
                                        "bold",

                                    fontSize:
                                        14

                                }}
                            >
                                Total PA+T/F
                            </Typography>

                        </Box>


                        {/* ==================================================
                            MOBILE COLUMN HEADER
                        ================================================== */}

                        <Box
                            sx={{

                                display: {

                                    xs:
                                        "block",

                                    sm:
                                        "none"

                                },

                                px: 0.75,

                                py: 0.75,

                                mb: 0.5,

                                backgroundColor:
                                    "#fafafa",

                                border:
                                    "1px solid #ddd",

                                borderRadius:
                                    1

                            }}
                        >

                            <Typography
                                sx={{

                                    fontSize:
                                        "0.75rem",

                                    color:
                                        "text.secondary",

                                    fontWeight:
                                        600,

                                    textAlign:
                                        "center"

                                }}
                            >
                                Salary Details
                            </Typography>

                        </Box>


                        {/* ==================================================
                            DATA
                        ================================================== */}

                        {loading ? (

                            <Box
                                sx={{

                                    py: 7,

                                    display:
                                        "flex",

                                    justifyContent:
                                        "center",

                                    alignItems:
                                        "center"

                                }}
                            >

                                <CircularProgress />

                            </Box>

                        ) : (

                            <Box
                                sx={{

                                    width:
                                        "100%",

                                    border:
                                        "1px solid #ddd",

                                    borderRadius: {

                                        xs:
                                            1,

                                        sm:
                                            0

                                    },

                                    overflow:
                                        "hidden"

                                }}
                            >

                                {rows.length === 0 ? (

                                    <Box
                                        sx={{

                                            py: 5,

                                            textAlign:
                                                "center"

                                        }}
                                    >

                                        <Typography
                                            color="text.secondary"
                                        >
                                            No data available
                                        </Typography>

                                    </Box>

                                ) : (

                                    rows.map(
                                        renderSalaryRow
                                    )

                                )}

                            </Box>

                        )}


                        {/* ==================================================
                            TOTAL BAR
                        ================================================== */}

                        <Box
                            sx={{

                                mt: 1,

                                backgroundColor:
                                    "#c28a00",

                                color: "#fff",

                                p: {

                                    xs: 1,

                                    sm: 1.25

                                },

                                display:
                                    "grid",

                                gridTemplateColumns: {

                                    xs:
                                        "1fr",

                                    sm:
                                        "repeat(2, 1fr)"

                                },

                                gap: {

                                    xs: 1,

                                    sm: 1.5

                                },

                                alignItems:
                                    "center"

                            }}
                        >

                            {/* MECHANICAL */}

                            <Box
                                sx={{

                                    display:
                                        "flex",

                                    alignItems:
                                        "center",

                                    justifyContent:
                                        "space-between",

                                    gap: 1,

                                    minWidth: 0

                                }}
                            >

                                <Typography
                                    fontWeight="bold"
                                    sx={{

                                        fontSize: {

                                            xs:
                                                "0.78rem",

                                            sm:
                                                "0.9rem"

                                        }

                                    }}
                                >
                                    Mechanical Total
                                </Typography>


                                <Box
                                    sx={{

                                        backgroundColor:
                                            "#fff",

                                        color:
                                            "#000",

                                        minWidth: {

                                            xs:
                                                85,

                                            sm:
                                                110

                                        },

                                        px: {

                                            xs:
                                                1,

                                            sm:
                                                1.5

                                        },

                                        py: 0.5,

                                        textAlign:
                                            "right",

                                        fontWeight:
                                            700,

                                        borderRadius:
                                            0.5

                                    }}
                                >

                                    {formatNumber(
                                        mechanicalTotal
                                    )}

                                </Box>

                            </Box>


                            {/* ELECTRICAL */}

                            <Box
                                sx={{

                                    display:
                                        "flex",

                                    alignItems:
                                        "center",

                                    justifyContent:
                                        "space-between",

                                    gap: 1,

                                    minWidth: 0

                                }}
                            >

                                <Typography
                                    fontWeight="bold"
                                    sx={{

                                        fontSize: {

                                            xs:
                                                "0.78rem",

                                            sm:
                                                "0.9rem"

                                        }

                                    }}
                                >
                                    Electrical Total
                                </Typography>


                                <Box
                                    sx={{

                                        backgroundColor:
                                            "#fff",

                                        color:
                                            "#000",

                                        minWidth: {

                                            xs:
                                                85,

                                            sm:
                                                110

                                        },

                                        px: {

                                            xs:
                                                1,

                                            sm:
                                                1.5

                                        },

                                        py: 0.5,

                                        textAlign:
                                            "right",

                                        fontWeight:
                                            700,

                                        borderRadius:
                                            0.5

                                    }}
                                >

                                    {formatNumber(
                                        electricalTotal
                                    )}

                                </Box>

                            </Box>

                        </Box>


                       


                        <Divider
                            sx={{
                                mb: 1.5
                            }}
                        />



{/* TOTAL CALCULATION NOTE */}
<Box
    sx={{
        mt: 1.5,
        mb: 1.5,
        p: 1.5,
        border: "1px dashed #90a4ae",
        borderRadius: 1.5,
        backgroundColor: "#f8fafc",
    }}
>
    <Typography
        variant="subtitle2"
        fontWeight="bold"
        color="primary"
        sx={{ mb: 1 }}
    >
        Calculation Note
    </Typography>

    <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Mechanical Total</strong> = Mechanical + PCO
        - Incentive Mechanical
    </Typography>

    <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Electrical Total</strong> = Electrical
        - Incentive Electrical
    </Typography>

    <Typography variant="body2">
        <strong>Grand Total</strong> = Mechanical Total
        + Electrical Total + RPF + Shop Clerk + BTC + CNTXR
    </Typography>
</Box>
  <Divider
                            sx={{
                                mb: 1.5
                            }}
                        />

                        {/* ==================================================
                            SAVE BUTTON
                        ================================================== */}

                        <Box
                            sx={{

                                display:
                                    "flex",

                                justifyContent:
                                    "center",

                                width:
                                    "100%"

                            }}
                        >

                            <Button
                                fullWidth

                                variant="contained"

                                startIcon={

                                    saving

                                        ? (

                                            <CircularProgress
                                                size={18}
                                                color="inherit"
                                            />

                                        )

                                        : (

                                            <SaveIcon />

                                        )

                                }

                                disabled={

                                    saving ||

                                    loading ||

                                    rows.length === 0

                                }

                                onClick={
                                    handleSave
                                }

                                sx={{

                                    width: {

                                        xs:
                                            "100%",

                                        sm:
                                            180

                                    },

                                    minHeight:
                                        44,

                                    fontWeight:
                                        "bold",

                                    backgroundColor:
                                        "#ffff00",

                                    color:
                                        "#e00000",

                                    border:
                                        "2px solid #e00000",

                                    "&:hover":
                                        {

                                            backgroundColor:
                                                "#ffff66"

                                        }

                                }}
                            >

                                {saving

                                    ? "Saving..."

                                    : "Save"

                                }

                            </Button>

                        </Box>

                    </CardContent>

                </Card>

            </Box>


            {/* ==================================================
                MESSAGE
            ================================================== */}

            <Snackbar

                open={
                    message.open
                }

                autoHideDuration={
                    4000
                }

                onClose={() =>
                    setMessage(
                        prev => ({

                            ...prev,

                            open:
                                false

                        })
                    )
                }

                anchorOrigin={{

                    vertical:
                        "bottom",

                    horizontal:
                        "center"

                }}

            >

                <Alert

                    severity={
                        message.severity
                    }

                    variant="filled"

                    onClose={() =>
                        setMessage(
                            prev => ({

                                ...prev,

                                open:
                                    false

                            })
                        )
                    }

                >

                    {message.text}

                </Alert>

            </Snackbar>

        </Box>

    );

}

